import { FormProps } from './types';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@shared/components/ui/form';
import { cn } from '@shared/lib/utils';
import { ReactNode } from 'react';
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import Text from '../content/text';
import { Checkbox } from '../ui/checkbox';

export interface CheckboxMultipleOption {
  label: string;
  value: string;
  /** Optional icon rendered to the left of the label. */
  icon?: ReactNode;
  /** Optional supporting copy rendered beneath the label. */
  description?: ReactNode;
  /** When true the option cannot be toggled. */
  disabled?: boolean;
}

interface CheckboxMultipleFormFieldProps extends FormProps {
  options: CheckboxMultipleOption[];
  /** Extra classes merged onto each option card (e.g. to restyle borders/background per flow). */
  optionClassName?: string;
}

export default function CheckboxMultipleFormField({
  name,
  options,
  optionClassName,
}: CheckboxMultipleFormFieldProps) {
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        return (
          <div className="space-y-2">
            {options.map((option) => (
              <FormItem key={option.value}>
                <FormControl>
                  <CustomCheckboxOption
                    field={field}
                    option={option}
                    optionClassName={optionClassName}
                  />
                </FormControl>
              </FormItem>
            ))}
          </div>
        );
      }}
    />
  );
}

interface CustomCheckboxOptionProps {
  option: CheckboxMultipleOption;
  field: ControllerRenderProps<FieldValues, string>;
  optionClassName?: string;
}

function CustomCheckboxOption({
  field,
  option,
  optionClassName,
}: CustomCheckboxOptionProps) {
  const { label, value, icon, description, disabled } = option;
  const hasDetail = !!icon || !!description;
  return (
    <FormLabel>
      <Checkbox
        showCheckmark={false}
        disabled={disabled}
        checked={field.value?.includes(value)}
        onCheckedChange={(checked) => {
          return checked
            ? field.onChange([...(field.value ?? []), value])
            : field.onChange(field.value?.filter((v: string) => v !== value));
        }}
        className={cn(
          'ml-auto h-16 w-full border-gray-100 rounded-md data-[state=checked]:border-teal-600 data-[state=checked]:bg-teal-100 data-[state=checked]:text-gray-900',
          hasDetail && 'flex h-auto items-center gap-3 px-4 py-3 text-left',
          optionClassName
        )}
      >
        {hasDetail ? (
          <>
            {icon}
            <span className="flex flex-col gap-1">
              <Text className="font-medium">{label}</Text>
              {description && (
                <Text variant="subtle" className="text-sm">
                  {description}
                </Text>
              )}
            </span>
          </>
        ) : (
          label
        )}
      </Checkbox>
    </FormLabel>
  );
}
